import { ApiResponseType } from '../types/ApiResponseTypes';
import { clearUserData } from './common';
import sendError from './sendError';
import trackEvent from './trackEvent';

const apiUrl = process.env.NEXT_PUBLIC_API_URL;
const publicApis = [
  'login',
  'register',
  'getResetPasswordToken',
  'resetPassword',
];

const publicPages = [
  '/ship-managers',
  '/service-suppliers',
  '/support',
  '/about-us',
  '/terms-and-conditions',
  '/register',
  '/privacy-policy',
  '/login',
  '/contact',
  '/password-reset',
  '/new-password',
  '/media-page',
  '/guides',
  '/guides/setting-up-an-account',
  '/guides/add-a-new-ship-guide',
  '/guides/edit-a-ship-details',
  '/guides/submit-a-request-guide',
  '/guides/edit-a-service-request',
  '/guides/handling-a-service-request',
];

const delay = (time: number) =>
  new Promise((resolve) => {
    setTimeout(() => resolve(true), time);
  });

const request = async (query: string): Promise<ApiResponseType> => {
  const sessionCreatedAt =
    (typeof window !== 'undefined' &&
      window.localStorage.getItem('sessionCreatedAt')) ||
    '';
  const sessionExpiration = new Date(sessionCreatedAt);
  sessionExpiration.setDate(sessionExpiration.getDate() + 7);

  // if the session (which expires at the same time as the session cookie) has "expired", then dont bother doing API calls
  // but allow publicAPIs to be called
  if (
    (sessionCreatedAt && new Date() < sessionExpiration) ||
    publicApis.some((a) => query.includes(a))
  ) {
    const response = await fetch(`${apiUrl}/graphql`, {
      method: 'POST',
      mode: 'cors',
      redirect: 'follow',
      credentials: 'include',
      headers: new Headers({
        'Content-Type': 'application/json',
      }),
      body: JSON.stringify({
        query,
      }),
    });

    if (response.status === 401 && window.location.pathname !== '/') {
      window.location.href = '/';
    }

    const responseJson = await response.json();

    if (responseJson.errors) {
      if (
        responseJson.errors.some((error) =>
          error.message.toLowerCase().includes('provide query')
        )
      ) {
        await sendError('The query we sent to the BE is empty', query);
      } else if (
        responseJson.errors[0].message === 'Forbidden' ||
        responseJson.errors[0].message === 'Invalid Session'
      ) {
        // dont send error if the user was an admin
        if (!window.localStorage.getItem('wasAdmin')) {
          // on forbidden, send the error to BE
          await sendError('Forbidden', query);
          // track event for forbidden
          trackEvent('Forbidden', {
            query,
            errorMessage: responseJson.errors[0].message,
            sessionCreatedAt,
            sessionExpiration: sessionExpiration.toISOString(),
          });
        }

        if (responseJson.errors[0].message === 'Invalid Session') {
          // clear localstorage
          clearUserData();
        }

        // if the user requests a non-public page, send him to homepage
        if (
          !publicPages.includes(window.location.pathname) &&
          window.location.pathname !== '/'
        ) {
          window.location.href = '/';
        }
      } else if (responseJson.errors[0].message === 'Session not found') {
        window.location.href = '/?message=sessionExpired&type=warning';
      }
    }

    return responseJson;
  }

  // the user is not logged in
  // if the user requests a non-public page, send him to homepage with a toast message
  if (
    !publicPages.includes(window.location.pathname) &&
    window.location.pathname !== '/'
  ) {
    window.location.href = '/?message=sessionExpired&type=warning';
    await delay(10000);
  }

  return { data: {} };
};

export default request;
